import { QueryResult, useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { READ_CUSTOMER_PMCS_DETAILS } from 'graphql/queries/readCustomerPmcsDetails';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { Charger, ParkzonePmcUnion } from 'types/common';
import {
  CountryCode,
  Maybe,
  Pmc,
  Query,
  QueryParkZoneArgs,
  QueryReadCustomerPmcsArgs,
  UnifiedPermit,
} from 'types/generatedSchemaTypes';
import { ParkNavProps } from 'types/navigation';
import { parseFeatureFlagObject, TypeDictionary } from 'utils/featureFlagUtils';
import Logger from 'utils/Logger';
import { isPermitForParkingZone, isPmcVisible } from 'utils/permitUtils';
import useParkingZoneWithEVCheck from './useParkingZoneWithEVCheck';

export type ShortTermParkingWizard = {
  parkingZoneResult?: QueryResult<Query, QueryParkZoneArgs>;
  isLoadingParkZoneDetails?: boolean;
  hasEVCharge?: boolean;
  mySalesOrdersForZone?: Maybe<UnifiedPermit>[];
  salesOrdersForZoneAndVehicle?: UnifiedPermit[];
  selectedEvCharger?: Charger;
  selectedParkzonePmcUnion?: ParkzonePmcUnion;
  setSelectedEvCharger: (charger?: Charger) => void;
  setSelectedParkzonePmcUnion: (ticket: ParkzonePmcUnion) => void;
  isOngoingParking: boolean;
  availablePermits?: Maybe<Pmc[]>;
};

const useShortTermParkingWizard = (): ShortTermParkingWizard => {
  const { replace: navigatePark } = useNavigation<ParkNavProps>();
  const { parkingConditions, setParkingConditions } = useParkingStore(
    (store) => store
  );
  const {
    customer: { permits, householdBenefits },
  } = useContext(AccountContext);
  const { sessionId: ongoingParkingSessionId } = useParkingStore(
    (state) => state.ongoingParkingSession
  );

  const { selectedParkingVehiclePlate } = useParkingStore((state) => state);
  const isParkingZoneSet = parkingConditions.selectedParkingZoneId?.length > 0;
  const [selectedEvCharger, setSelectedEvCharger] = useState<Charger>();

  const selectedParkzonePmcUnion = useParkzoneStore(
    (state) => state.selectedParkzonePmcUnion
  );
  const setSelectedParkzonePmcUnion = useCallback(
    (ticket: ParkzonePmcUnion) => {
      useParkzoneStore.getState().setSelectedParkzonePmcUnion(ticket);
    },
    []
  );

  const {
    flags: { sales_order_permits },
  } = useContext(FeatureFlagsContext);
  const ongoingParkingSheetExp = useParkingStore(
    (state) => state.selectedOngoingSession
  );
  const parkingZoneResult = useParkingZoneWithEVCheck(
    parkingConditions.selectedParkingZoneId,
    'cache-and-network',
    // Charging stations are needed to resolve EV charging pricing, which drives
    // the private-access gate on the park detail buttons. The underlying query
    // is still skipped for zones without `parkingMethods.evCharging`.
    true
  );

  const { parkZone } = parkingZoneResult?.data || {};

  const { parkingMethods, countryCode } = parkZone || {};
  const { data: permitData, loading: permitLoading } = useQuery<
    Query,
    QueryReadCustomerPmcsArgs
  >(READ_CUSTOMER_PMCS_DETAILS, {
    variables: {
      input: {
        parkingZoneUids: [parkingConditions.selectedParkingZoneId],
        permitType: 'SALES_ORDER',
      },
    },
    skip: !isParkingZoneSet,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  const isLoadingParkZoneDetails = useMemo(
    () => permitLoading || parkingZoneResult?.loading,
    [permitLoading, parkingZoneResult?.loading]
  );

  const isPermitVisible = (permit: UnifiedPermit) => {
    return (
      permit?.orderStatus === 'ACTIVE' ||
      permit?.orderStatus === 'UPDATING' ||
      permit.permitType === 'WAITING_LIST'
    );
  };

  const mySalesOrdersForZone = useMemo(() => {
    return (
      permits?.filter(
        (sOrder) =>
          isPermitForParkingZone(
            parkingConditions.selectedParkingZoneId,
            sOrder
          ) && isPermitVisible(sOrder)
      ) ?? []
    );
  }, [parkingConditions.selectedParkingZoneId, permits]);

  const filterByLicensePlate = (
    sOrder: UnifiedPermit,
    licensePlateText: string
  ) => {
    return sOrder?.orderLines?.some((orderLine) =>
      orderLine?.registrationPlates?.some(
        (licensePlate) => licensePlate?.plateText === licensePlateText
      )
    );
  };

  const salesOrdersForZoneAndVehicle = useMemo(() => {
    if (!mySalesOrdersForZone || !selectedParkingVehiclePlate) {
      return [];
    }

    return mySalesOrdersForZone.filter((sOrder) =>
      filterByLicensePlate(sOrder, selectedParkingVehiclePlate)
    );
  }, [mySalesOrdersForZone, selectedParkingVehiclePlate]);

  const showAvailablePermits = useMemo(() => {
    const countryObject = parseFeatureFlagObject<
      TypeDictionary<CountryCode, boolean>
    >(sales_order_permits, 'sales_order_permits');
    return !!countryCode && countryObject[countryCode];
  }, [countryCode, sales_order_permits]);

  const hasEVCharge = useMemo(() => {
    return Boolean(parkingMethods?.evCharging);
  }, [parkingMethods?.evCharging]);

  const handleInvalidParkingZone = useCallback(() => {
    useActiveElementStore.getState().setActiveElement('');
    setParkingConditions({ selectedParkingZoneId: '' });
    showToast('error.getParkZoneFailed', 'error');
    navigatePark('park');
    Logger.error(
      `Park zone ${parkingConditions.selectedParkingZoneId} data not defined | Error: ${parkingZoneResult?.error}`
    );
  }, [
    parkingConditions.selectedParkingZoneId,
    navigatePark,
    setParkingConditions,
    parkingZoneResult?.error,
  ]);

  const setCharger = useCallback((charger?: Charger) => {
    setSelectedEvCharger(charger);
    useActiveElementStore.getState().setActiveCharger(charger);
  }, []);

  const handleParkingZoneChange = useCallback(() => {
    if (ongoingParkingSheetExp || isLoadingParkZoneDetails) {
      return;
    }
    if (
      parkingConditions.selectedParkingZoneId &&
      !parkingZoneResult?.data?.parkZone?.uid
    ) {
      handleInvalidParkingZone();
    }

    setCharger(undefined);
  }, [
    parkingConditions.selectedParkingZoneId,
    handleInvalidParkingZone,
    isLoadingParkZoneDetails,
    ongoingParkingSheetExp,
    parkingZoneResult?.data?.parkZone?.uid,
    setCharger,
  ]);

  useEffect(handleParkingZoneChange, [handleParkingZoneChange]);

  return {
    parkingZoneResult,
    isLoadingParkZoneDetails,
    hasEVCharge,
    mySalesOrdersForZone,
    salesOrdersForZoneAndVehicle,
    selectedParkzonePmcUnion,
    selectedEvCharger,
    setSelectedParkzonePmcUnion,
    setSelectedEvCharger: setCharger,
    isOngoingParking: !!ongoingParkingSessionId,
    availablePermits: permitData?.readCustomerPmcs?.filter(
      (pmc) =>
        showAvailablePermits && isPmcVisible(pmc, householdBenefits ?? [])
    ),
  };
};

export default useShortTermParkingWizard;
